import React, { lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MainContent } from '@bbkAdminComponents/app-layout/main-content';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import { TrialSignupPage3 } from '@bbkAdminComponents/trial-signup/TrialSignupPage3';
import {
  selectApp,
  selectAppState,
  selectCompany,
  selectUser,
} from '@bbkAdminRedux/app/selectors';
import { invitationSlice } from '@bbkAdminRedux/rtkq/invitation.slice';
import { AuthorizedRouter } from '@bbkAdminRouter/authorized.router';
import { LogoutPage } from '@bbkAdminRouter/logout.page';
import { SignupPaths } from '@bbkAdminRouter/signup-paths';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
} from '@bbkShared/react-router';

const CompanySelectorPage = lazy(
  () => import('@bbkAdminComponents/CompanySelectorPage')
);
const InternalRouter = lazy(
  () => import('@bbkAdminComponents/internal/internal-router')
);

export const LoggedInRouter: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const app = useSelector(selectApp);
  const { data: invites = [], isFetching: isFetchingInvites } =
    invitationSlice.endpoints.getPendingInvitationsByEmail.useQuery();
  const loadedNecessaryData = useSelector(selectAppState).loadedNecessaryData;

  useEffect(() => {
    const pathname = location.pathname;
    const adminAppPagesRegex =
      /^\/(trial_signup|company|internal|logout|cbidp-exchange)/;
    if (!adminAppPagesRegex.test(pathname)) {
      history.push(LogInPaths.company);
    }
  }, [history, location.pathname]);

  useEffect(() => {
    // No company or app set yet, but there is a user with companies. Need to go to the selector.
    if (!(company && app) && user && user.companies.length > 1) {
      if (
        location.pathname === LogInPaths.signIn ||
        location.pathname === LogInPaths.company ||
        location.pathname === `${LogInPaths.company}/`
      ) {
        history.push(LogInPaths.selector);
      }
    }
  }, [app, company, history, location.pathname, user]);

  useEffect(() => {
    // If a logged-in user has no companies associated with them, default to the Selector page
    if (user && user.companies.length === 0) {
      history.push(LogInPaths.selector);
    }
  }, [history, user]);

  // Still trying to set all items, wait until finished to choose what to show
  // FIXME: isFtechingInvites is critical here. It blocks following Routers to load.
  // Need to figure out how to not rely on isFetchingInvites
  if (!loadedNecessaryData || isFetchingInvites) {
    return <SuspenseLoader message=" " />;
  }

  return (
    <Suspense
      fallback={
        <MainContent>
          <SuspenseLoader message=" " />
        </MainContent>
      }
    >
      <Switch>
        <Route path={SignupPaths.trialSignup3}>
          <TrialSignupPage3 />
        </Route>

        <Route path={LogInPaths.mfaReset}>
          <LogoutPage />
        </Route>

        <Route path={LogInPaths.logout}>
          <LogoutPage />
        </Route>

        <Route path={LogInPaths.selector} exact>
          <MainContent>
            <CompanySelectorPage />
          </MainContent>
        </Route>

        <Route path="/internal/">
          <MainContent>
            <InternalRouter />
          </MainContent>
        </Route>

        <Route>
          <AuthorizedRouter />
        </Route>
      </Switch>
    </Suspense>
  );
};
